// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The single type-discipline unit for rendering a numeric IR Cell. This is the ONLY place a
// number reaches the screen, so the never-fabricate rules live here, once:
//
//   * status 'not_found' (value null)         -> an explicit "Not found — <note>" sentinel.
//                                                NEVER 0, NEVER blank. The reader sees the gap.
//   * status 'quarantined_forward_read' /
//     'temporal_quarantine'                    -> the value renders ONLY when the caller is a
//                                                labelled forward-read panel (allowQuarantined).
//                                                Anywhere else it shows a locked placeholder, no number.
//   * status 'ok'                             -> the formatted number, wrapped in a provenance
//                                                tooltip, with a needs-review dot + flag chips.
//
// CellValue accepts NO `.text` (a Cell has none — enforced by the `Cell` type), so prose can
// never leak through the numeric path.

import { cn } from "@/lib/utils";
import { Lock } from "lucide-react";
import type { Cell } from "@/types/ir";
import { ProvenanceTooltip } from "./ProvenanceTooltip";
import { formatCellValue, signTone, toneDot, toneText, type Tone } from "./labels";

export function CellValue({
  cell,
  digits = 2,
  signed = false,
  allowQuarantined = false,
  className,
}: {
  cell: Cell | undefined;
  digits?: number;
  // when true the sign of the number drives the colour tone (excess / drift / attribution)
  signed?: boolean;
  // set ONLY by a labelled forward-read panel — permits quarantined values to render
  allowQuarantined?: boolean;
  className?: string;
}) {
  if (!cell) return <span className={cn("font-body text-muted-foreground", className)}>—</span>;

  // --- NOT FOUND: explicit sentinel, never a zero or a blank ------------------
  if (cell.status === "not_found" || cell.value == null) {
    const note = cell.note ? cell.note : "no clean source";
    return (
      <span
        className={cn("font-body text-xs italic text-muted-foreground", className)}
        title={note}
      >
        Not found — {note}
      </span>
    );
  }

  // --- QUARANTINED: value hidden unless inside a labelled forward-read panel ---
  const quarantined =
    cell.status === "quarantined_forward_read" || cell.status === "temporal_quarantine";
  if (quarantined && !allowQuarantined) {
    return (
      <span
        className={cn("inline-flex items-center gap-1 font-body text-xs text-muted-foreground", className)}
        title="Quarantined forward-read — renders only in a labelled forward-read panel, never as Q2 attribution."
      >
        <Lock className="h-3 w-3" aria-hidden /> forward-read
      </span>
    );
  }

  // --- OK (or quarantined-in-panel): the number, audited ---------------------
  const tone: Tone = signed ? signTone(cell.value) : "neutral";
  const text = formatCellValue(cell.value, cell.basis, digits);

  return (
    <span className={cn("inline-flex items-center gap-1.5 font-body tabular-nums", className)}>
      <ProvenanceTooltip cell={cell}>
        <span className={cn(quarantined ? "text-muted-foreground" : toneText(tone))}>{text}</span>
      </ProvenanceTooltip>
      {cell.needs_review && (
        <span
          className={cn("h-1.5 w-1.5 rounded-full shrink-0", toneDot("attention"))}
          aria-label="needs review"
          title="Needs review — a blocking flag references this cell"
        />
      )}
      {Array.isArray(cell.flag_ids) &&
        cell.flag_ids.map((fid) => (
          <span
            key={fid}
            className="font-body text-[10px] rounded px-1 bg-muted text-muted-foreground"
            title={`Flag ${fid}`}
          >
            {fid}
          </span>
        ))}
    </span>
  );
}
